import "./urlShortener.scss";
import * as React from "react";
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Grid,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { GlobalContext } from "../../components/GlobalStateProvide/GlobalStateProvider";
import { checkPermission } from "../../utils/userPermissions";
import LinkIcon from "@mui/icons-material/Link";
import ModalComponent from "../../components/ModalComponent/ModalComponent";
import Unauthorized from "../../components/unauthorized/Unauthorized";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Formik, Form, Field } from "formik";
import { createLinkValidatonSchema } from "../../utils/Validations";
import {
  getRequestForLinkShortener,
  postRequestForLinkShortener,
} from "../../utils/ApiRequests";

const UrlShortener = () => {
  const DEFAULT_SORTING = [{ id: "createdAt", desc: true }];
  const [data, setData] = useState([]);
  const [showLoader, setShowLoader] = useState(true);


  const [rowCount, setRowCount] = useState(0);
  const [loadingDataInGrid, setLoadingDataInGrid] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");

  const [canRead, setCanRead] = useState(false);
  const [canCreate, setCanCreate] = useState(false);

  const [isAccess, setisAccess] = useState([]);
  const [isIncludesValue, setisIncludesValue] = useState(null);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [isRefetching, setIsRefetching] = useState(false);
  const [visitHistoryModal, setVisitHistoryModal] = useState({ open: false, title: '', history: [] });
  const [recentlyCreatedUrl, setRecentlyCreatedUrl] = useState(null);
  const [pageTokens, setPageTokens] = useState({ 0: null });
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isTokenPagination, setIsTokenPagination] = useState(false);
  const [knownPageCount, setKnownPageCount] = useState(1);

  const { handleSnackBar } = useContext(GlobalContext);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const initialValues = {
    title: "",
    link: "",
  };

  const mergeLatestCreatedUrl = (rows, latestRow) => {
    if (!latestRow?.shortCode) return Array.isArray(rows) ? rows : [];
    const list = Array.isArray(rows) ? rows : [];
    return [latestRow, ...list.filter((item) => item?.shortCode !== latestRow.shortCode)];
  };

  const getSortedRows = (rows, activeSorting) => {
    const list = Array.isArray(rows) ? [...rows] : [];
    const activeSort = activeSorting?.[0] || DEFAULT_SORTING[0];
    const { id, desc } = activeSort;

    return list.sort((a, b) => {
      let aValue = 0;
      let bValue = 0;

      if (id === "clickCount") {
        aValue = a?.visitHistory?.length || 0;
        bValue = b?.visitHistory?.length || 0;
      } else if (id === "lastClickedAt") {
        const aHistory = a?.visitHistory || [];
        const bHistory = b?.visitHistory || [];
        aValue = aHistory.length ? new Date(aHistory[aHistory.length - 1].timestamp).getTime() : 0;
        bValue = bHistory.length ? new Date(bHistory[bHistory.length - 1].timestamp).getTime() : 0;
      } else {
        aValue = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        bValue = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      }

      return desc ? bValue - aValue : aValue - bValue;
    });
  };

  const handleSubmit = async (values, { resetForm }) => {
    const { title, link, custom_link } = values;
    try {
      setFormSubmitted(true);
      let res = await postRequestForLinkShortener({
        urlTitle: title,
        longUrl: link,
        customShortCode: !custom_link ? null : custom_link.trim().replaceAll(" ", "-")
      });
      res = res.shortUrl;
      const shortCode = res ? new URL(res).searchParams.get("u") : "";
      const createdAt = new Date().toISOString();
      setRecentlyCreatedUrl({
        shortCode,
        originalUrl: link,
        urlTitle: title,
        createdAt,
        updatedAt: createdAt,
        visitHistory: [],
      });
      setRowCount((prev) => prev + 1);
      setPageTokens({ 0: null });
      setHasMorePages(false);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      resetForm();
      setOpenModal(false);
      handleSnackBar(true, "Short Link is generated successfully", "success");
      setShowGenerateLinkModal(true);
      setGeneratedLink(res);
    } catch (error) {
      console.log(error);
    } finally {
      setFormSubmitted(false);
    }
  };

  const fetchData = async () => {
    const hasActiveFilters = !!globalFilter || columnFilters.length > 0;
    if (hasActiveFilters) {
      setData([]);
    }
    if (!data.length) {
      setLoadingDataInGrid(true);
    } else {
      setIsRefetching(true);
    }

    try {
      const filterParams = {};
      // Global search maps to search param (searches title, shortCode, originalUrl)
      if (globalFilter) {
        let searchValue = globalFilter;
        try {
          const parsedUrl = new URL(searchValue);
          if (parsedUrl.hostname === 'jyti.in') {
            const extracted = parsedUrl.searchParams.get('u');
            if (extracted) searchValue = extracted;
          }
        } catch {}
        filterParams.search = searchValue;
      }
      // Column filters
      columnFilters.forEach((filter) => {
        let value = filter.value;
        try {
          const parsedUrl = new URL(value);
          if (parsedUrl.hostname === 'jyti.in') {
            const extracted = parsedUrl.searchParams.get('u');
            if (extracted) value = extracted;
          }
        } catch {}
        filterParams[filter.id] = value;
      });
      // Sorting
      if (sorting.length > 0) {
        const colId = sorting[0].id;
        filterParams.sortBy = colId === 'clickCount' ? 'views' : colId === 'lastClickedAt' ? 'lastClicked' : 'createdAt';
        filterParams.sortOrder = sorting[0].desc ? 'desc' : 'asc';
      }

      const activeSort = sorting?.[0] || DEFAULT_SORTING[0];
      const canUseTokenBasedPaging =
        activeSort.id === "createdAt" &&
        columnFilters.length === 0 &&
        !globalFilter;

      setIsTokenPagination(canUseTokenBasedPaging);

      if (canUseTokenBasedPaging) {
        const currentPageToken = pageTokens[pagination.pageIndex];
        if (pagination.pageIndex > 0 && typeof currentPageToken === "undefined") {
          setData([]);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          setPageTokens({ 0: null });
          setHasMorePages(false);
          setKnownPageCount(1);
          setLoadingDataInGrid(false);
          setIsRefetching(false);
          return;
        }

        const params = new URLSearchParams({
          list: "true",
          limit: String(pagination.pageSize),
          sortBy: "createdAt",
          sortOrder: activeSort.desc ? "desc" : "asc",
        });

        if (currentPageToken) {
          params.set("pageToken", currentPageToken);
        }

        const response = await getRequestForLinkShortener(`?${params.toString()}`);
        const rows = pagination.pageIndex === 0
          ? mergeLatestCreatedUrl(response?.urls || [], recentlyCreatedUrl)
          : (response?.urls || []);
        const nextPageExists = Boolean(response?.nextPageToken);
        const discoveredPageCount = response?.totalPages || (pagination.pageIndex + (nextPageExists ? 2 : 1));

        setData(rows);
        setHasMorePages(nextPageExists);
        setKnownPageCount((prev) => Math.max(prev, discoveredPageCount));
        setPageTokens((prev) => {
          const nextTokens = { ...prev, [pagination.pageIndex]: currentPageToken ?? null };
          if (response?.nextPageToken) {
            nextTokens[pagination.pageIndex + 1] = response.nextPageToken;
          } else {
            delete nextTokens[pagination.pageIndex + 1];
          }
          return nextTokens;
        });
        setRowCount(response?.totalItems || 0);
      }

      if (!canUseTokenBasedPaging) {
        const filterQueryString = new URLSearchParams(filterParams).toString();
        const response = await getRequestForLinkShortener(`?list=true&skip=${pagination.pageIndex}&limit=${pagination.pageSize}&${filterQueryString}`);
        const incomingRows = pagination.pageIndex === 0
          ? mergeLatestCreatedUrl(response?.urls || [], recentlyCreatedUrl)
          : (response?.urls || []);

        setData(getSortedRows(incomingRows, sorting));
        setHasMorePages(false);
        setPageTokens({ 0: null });
        setKnownPageCount(Math.max(1, Math.ceil((response?.totalItems || 0) / pagination.pageSize)));
        setRowCount(response?.totalItems || 0);
      }

      setLoadingDataInGrid(false);
      setShowLoader(false);
    } catch (error) {
      handleSnackBar(
        true,
        error?.response?.data?.message || error?.message,
        "error"
      );
      setLoadingDataInGrid(false);
    }
    setLoadingDataInGrid(false);
    setIsRefetching(false);
    setShowLoader(false);
  };

  const columns = [
    {
      accessorKey: "id",
      header: "S.No.",
      size: 120,
      enableColumnFilter: false,
      Cell: ({ row, table }) => {
        const pageIndex = table.getState().pagination.pageIndex;
        const pageSize = table.getState().pagination.pageSize;
        const rowIndex = row.index;
        return <span>{rowIndex + 1 + pageIndex * pageSize}</span>;
      },
    },
    {
      accessorFn: (row) => {
        const title = row?.urlTitle;
        const isInvalid = !title || title.trim() === '' || /^(undefined\s*)+$/i.test(title.trim());
        return <span style={isInvalid ? { color: '#aaa', fontStyle: 'italic' } : {}}>{isInvalid ? '— No title —' : title}</span>;
      },
      accessorKey: "urlTitle",
      header: "Title",
      size: 350,
      id: "urlTitle",
    },
    {
      accessorKey: "shortCode",
      header: "Short Code",
      size: 280,
      id: "shortCode",
      muiFilterTextFieldProps: { placeholder: 'Search by short code…' },
      accessorFn: (row) => row?.shortCode || '',
      Cell: ({ row }) => {
        const shortLink = row.original.shortCode ? `https://jyti.in/?u=${row.original.shortCode}` : '';
        return <span style={{ color: 'blue', cursor: 'pointer' }} onClick={() => copyText(shortLink)}>{shortLink}</span>;
      },
    },
    {
      accessorFn: (row) => {
        return (<span>{row?.originalUrl}</span>)
      },
      accessorKey: "originalUrl",
      header: "URL",
      size: 350,
      id: "originalUrl",
    },
    {
      accessorKey: "clickCount",
      header: "Views",
      size: 100,
      enableColumnFilter: false,
      enableSorting: true,
      Cell: ({ row }) => {
        const views = row.original.visitHistory?.length ?? 0;
        return (
          <Tooltip title={views > 0 ? 'Click to see visit details' : 'No visits yet'}>
            <span
              onClick={() => views > 0 && setVisitHistoryModal({ open: true, title: row.original.urlTitle || '', shortCode: row.original.shortCode, history: row.original.visitHistory })}
              style={{ fontWeight: 600, color: views > 0 ? '#1976d2' : '#999', cursor: views > 0 ? 'pointer' : 'default', textDecoration: views > 0 ? 'underline' : 'none' }}
            >
              {views}
            </span>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "lastClickedAt",
      header: "Last Clicked",
      size: 200,
      enableColumnFilter: false,
      enableSorting: true,
      Cell: ({ row }) => {
        const history = row.original.visitHistory;
        if (!history || history.length === 0) return <span style={{ color: '#999' }}>—</span>;
        const last = history[history.length - 1].timestamp;
        return <span>{new Date(last).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>;
      },
    },
    {
      accessorKey: "createdAt",
      header: "Created At",
      size: 200,
      enableColumnFilter: false,
      enableSorting: true,
      Cell: ({ row }) => {
        const createdAt = row.original.createdAt || "--";
        return (
          <span>
            {new Date(createdAt).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
            })}
          </span>
        );
      },
    },

    {
      accessorKey: "z_action",
      header: "Action",
      size: 280,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        <Box
          style={{
            display: "flex",
            gap: "0.5rem",
            cursor: "pointer",
            alignItems: "center",
          }}
          onClick={() => {
            copyText(`https://jyti.in/?u=${row?.original?.shortCode}`);
          }}
        >
          <LinkIcon />
          <span> Copy Link</span>
        </Box>
    },
  ];

  useEffect(() => {
    fetchData();
  }, [
    columnFilters,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
  ]);

  useEffect(() => {
    if (!recentlyCreatedUrl || pagination.pageIndex !== 0) return;

    setData((prev) => mergeLatestCreatedUrl(prev, recentlyCreatedUrl).slice(0, pagination.pageSize));
  }, [recentlyCreatedUrl, pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    setPageTokens({ 0: null });
    setHasMorePages(false);
    setKnownPageCount(1);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [sorting, globalFilter, columnFilters]);

  const handlePaginationChange = (updater) => {
    setPagination((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      if (next.pageSize !== prev.pageSize) {
        setPageTokens({ 0: null });
        setHasMorePages(false);
        setKnownPageCount(1);
        return { ...next, pageIndex: 0 };
      }
      if (isTokenPagination) {
        const isMovingForward = next.pageIndex > prev.pageIndex;
        const nextTokenExists = typeof pageTokens[next.pageIndex] !== "undefined";
        if (isMovingForward && !hasMorePages && next.pageIndex > prev.pageIndex) {
          return prev;
        }
        if (isMovingForward && !nextTokenExists && next.pageIndex !== prev.pageIndex) {
          return prev;
        }
      }
      return next;
    });
  };

  const checkPermissionFunc = () => {
    const resourceToUpdate = "Payment Details";
    // console.log(JSON.parse(checkPermission(resourceToUpdate)));
    const permissions = checkPermission(resourceToUpdate);
    setCanCreate(permissions.canCreate);
    setCanRead(permissions.canRead);
  };

  const againCheckPermissions = () => {
    const checkAccesInArray = localStorage.getItem("resourceNames");
    setisAccess(checkAccesInArray);
    isIncludes();
  };

  const isIncludes = () => {
    setisIncludesValue(isAccess.includes("URL Shortener"));
  };

  useEffect(() => {
    checkPermissionFunc();
    againCheckPermissions();
  }, [isIncludesValue]);

  const table = useMaterialReactTable({
    columns,
    data: data || [],
    initialState: {
      showColumnFilters: true,
      sorting: DEFAULT_SORTING,
    },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    enableColumnResizing: true,
    enableGlobalFilter: true,
    enableSorting: true,
    muiSearchTextFieldProps: {
      placeholder: 'Search by title…',
      size: 'small',
    },
    muiPaginationProps: isTokenPagination ? {
      showFirstButton: false,
      showLastButton: false,
    } : undefined,
    muiLinearProgressProps: {
      style: {
        backgroundColor: "secondary",
      },
    },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: handlePaginationChange,
    onSortingChange: setSorting,
    rowCount,
    state: {
      columnFilters,
      globalFilter,
      isLoading: loadingDataInGrid,
      pagination,
      // showAlertBanner: isError,
      showSkeletons: isRefetching,
      showProgressBars: isRefetching,
      sorting,
    },
    renderBottomToolbarCustomActions: ({ table }) => {
      const { pageIndex, pageSize } = table.getState().pagination;
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" style={{ marginLeft: "10px" }}>
            {`Page ${pageIndex + 1} of ${Math.max(1, isTokenPagination ? knownPageCount : Math.ceil(rowCount / pageSize))}`}
          </Typography>
        </Box>
      );
    },
    renderTopToolbarCustomActions: ({ table }) => {
      const pinnedColumns = table.getState().columnPinning;
      return (
        <Box>
          <Chip
            label={`Left Pinned: ${pinnedColumns.left.length}`}
            color="primary"
            sx={{ mr: 1 }}
          />
          <Chip
            label={`Right Pinned: ${pinnedColumns.right.length}`}
            color="secondary"
          />
        </Box>
      );
    },
  });

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleCloseGenerateLinkModal = () => {
    setShowGenerateLinkModal(false);
    fetchData();
  };

  const copyText = (textToCopy) => {
    if (!canRead) {
      handleSnackBar(true, "You don't have permission to copy link", "error");
      return;
    }
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => handleSnackBar(true, "Link is copied", "success"))
      .catch((err) => handleSnackBar(true, err, "error"));
  };

  const handleCopyGeneratedLink = () => {
    copyText(generatedLink);
    handleCloseGenerateLinkModal();
  };

  return isIncludesValue ? (
    <Box className="urlShortenerSection">

      {/* Visit History Modal */}
      <ModalComponent
        openModal={visitHistoryModal.open}
        setOpenModal={(v) => setVisitHistoryModal(s => ({ ...s, open: v }))}
        modalTitle={`Visit History — ${(() => { const t = visitHistoryModal.title; return (!t || /^(undefined\s*)+$/i.test(t.trim())) ? visitHistoryModal.shortCode || 'Link' : t; })()}`}
        modalWidth="55%"
        handleOnCloseBtnClick={() => setVisitHistoryModal(s => ({ ...s, open: false }))}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            Total visits: <strong>{visitHistoryModal.history.length}</strong>
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 600 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Visited At (IST)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visitHistoryModal.history.map((visit, i) => (
                  <TableRow key={i} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{visit.sourceIp || '—'}</TableCell>
                    <TableCell>{visit.timestamp ? new Date(visit.timestamp).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </ModalComponent>

      <ModalComponent
        openModal={openModal}
        setOpenModal={setOpenModal}
        modalTitle="Create Link"
        modalWidth="35%"
        handleOnCloseBtnClick={handleCloseModal}
      >
        <Box style={{ padding: "15px" }}>
          <Formik
            initialValues={initialValues}
            validationSchema={createLinkValidatonSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ errors, values, setFieldValue, resetForm }) => (
              <Form className="create-url-link-form">
                <Grid container spacing={2} className="cstmFormGridUrlLink">
                  <Grid item xs={12} sm={12} md={12}>
                    <Box className="form-control">
                      <label className="form-label" htmlFor="title">
                        {" "}
                        Title <span className="must-fill-fields">*</span>
                      </label>
                      <Field
                        id="title"
                        type="text"
                        placeholder="Enter Title"
                        name="title"
                        className="form-input"
                        size="small"
                      />
                      {errors.title && (
                        <Box className="form-error">{errors.title}</Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <Box className="form-control">
                      <label className="form-label" htmlFor="link">
                        {" "}
                        Link <span className="must-fill-fields">*</span>
                      </label>
                      <Field
                        id="link"
                        type="text"
                        placeholder="Enter Link"
                        name="link"
                        className="form-input"
                        size="small"
                      />
                      {errors.link && (
                        <Box className="form-error">{errors.link}</Box>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={12} md={12}>
                    <Box className="form-control">
                      <label className="form-label" htmlFor="custom_link">
                        Custom Link
                      </label>
                      <Field
                        id="custom_link"
                        type="text"
                        placeholder="Enter Custom Link (eg. custom-link)"
                        name="custom_link"
                        className="form-input"
                        size="small"
                      />
                    </Box>
                  </Grid>

                </Grid>

                <Box className="form-button-section">
                  <Button
                    className="form-button"
                    variant="contained"
                    type="submit"
                    color="success"
                    disabled={formSubmitted}
                  >
                    Generate Link
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </ModalComponent>

      <ModalComponent
        openModal={showGenerateLinkModal}
        setOpenModal={setShowGenerateLinkModal}
        modalTitle="Generated Link"
        modalWidth="35%"
        handleOnCloseBtnClick={handleCloseGenerateLinkModal}
      >
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "15px",
            gap: "10px"
          }}
        >
          <Box> Link : <span style={{ color: "blue" }}>{generatedLink}</span></Box>
          <Button
            className="form-button"
            variant="contained"
            type="submit"
            color="success"
            onClick={handleCopyGeneratedLink}
          >
            Copy Link
          </Button>
        </Box>
      </ModalComponent>

      <Box className="urlShortenerHeader">
        <h3 className="formHeadings">JY Link Shortener</h3>
        {canCreate && (
          <Button variant="contained" color="success" onClick={handleOpenModal}>
            Create Link
          </Button>
        )}
      </Box>

      <Box sx={{ width: "100%" }}>
        {showLoader ? (
          <Box
            sx={{ width: "100%", height: "100%" }}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Box>
        ) : (
          <MaterialReactTable table={table} />
        )}
      </Box>
    </Box>
  ) : (
    <Unauthorized />
  );
};
export default UrlShortener;
